import { createContext, useState, useEffect, useCallback, useMemo } from 'react';

const THEME_STORAGE_KEY = 'fitsocial-theme-preference';

/**
 * Detect the user's system color-scheme preference.
 * @returns {'light' | 'dark'}
 */
const getSystemPreference = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';
  }
  return 'dark';
};

/**
 * Read persisted theme mode from localStorage.
 * Valid values: 'light', 'dark', 'system'.
 * Defaults to 'system' if nothing stored.
 * @returns {'light' | 'dark' | 'system'}
 */
const getInitialThemeMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable
  }
  return 'system';
};

export const ThemeProviderContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [systemPref, setSystemPref] = useState(getSystemPreference);

  // Persist theme mode to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch {
      // localStorage may be unavailable
    }
  }, [themeMode]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => {
      setSystemPref(e.matches ? 'light' : 'dark');
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /**
   * Set the theme mode explicitly.
   * @param {'light' | 'dark' | 'system'} mode
   */
  const setTheme = useCallback((mode) => {
    if (mode === 'light' || mode === 'dark' || mode === 'system') {
      setThemeMode(mode);
    }
  }, []);

  /**
   * Legacy toggle: cycles light -> dark -> system -> light.
   */
  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, []);

  // The resolved theme for rendering ('light' or 'dark')
  const theme = useMemo(() => {
    if (themeMode === 'system') return systemPref;
    return themeMode;
  }, [themeMode, systemPref]);

  const value = useMemo(() => ({
    theme,         // resolved: 'light' | 'dark'
    themeMode,     // user choice: 'light' | 'dark' | 'system'
    setTheme,      // set explicit mode
    toggleTheme,   // cycle through modes
  }), [theme, themeMode, setTheme, toggleTheme]);

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
};
